import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import { useLaunchReadiness } from "../../hooks/useLaunchReadiness";
import { CheckStatus, LaunchCheck } from "../../database/launch/launchReadiness";

// Persistent "steps to launch" strip. Renders at the top of every dashboard
// screen while the community is not yet launch ready, and disappears once
// every scored gate is green. Defaults to a slim collapsed bar (never a full
// dismiss) so it stays in view without dominating the screen; expanding shows
// only what is left to do, with finished steps tucked behind a toggle.

const Card = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto var(--space-4, 16px);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg, 12px);
  background: var(--color-surface-subtle, var(--color-surface));
  overflow: hidden;
`;

const HeaderBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--color-text);
  &:focus { outline: none; }
  &:hover { background: var(--color-surface-subtle); }
`;

const HeaderText = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-text);
`;

const Sub = styled.div`
  font-size: 12.5px;
  color: var(--color-text-tertiary);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
  color: var(--color-text-secondary);
`;

const Bar = styled.div`
  width: 84px;
  height: 5px;
  border-radius: 3px;
  background: var(--color-border);
  overflow: hidden;
`;

const Fill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: var(--color-accent);
  transition: width 0.3s ease;
`;

const Count = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-secondary);
  min-width: 44px;
  text-align: right;
`;

const Body = styled.div`
  padding: 4px 8px 8px;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
`;

const GroupTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: var(--color-text-tertiary);
  padding: 12px 12px 4px;
`;

const CompletedToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 4px 2px;
  padding: 6px 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  border-radius: var(--radius-md, 8px);
  &:focus { outline: none; }
  &:hover { color: var(--color-text-secondary); }
`;

const Row = styled.div<{ clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: var(--radius-md, 8px);
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: background 0.12s;
  &:hover { background: ${({ clickable }) => (clickable ? "var(--color-surface-subtle)" : "transparent")}; }
`;

const Dot = styled.span<{ status: CheckStatus }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ status }) =>
    status === "ready"
      ? "var(--color-success)"
      : status === "partial"
        ? "var(--color-warning, #e2b53d)"
        : status === "missing"
          ? "var(--color-border-strong, #6b6b6b)"
          : "transparent"};
  border: ${({ status }) =>
    status === "unknown" ? "1.5px dashed var(--color-border-strong, #6b6b6b)" : "none"};
  box-sizing: border-box;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

const RowItem = styled.span<{ done: boolean }>`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ done }) => (done ? "var(--color-text-secondary)" : "var(--color-text)")};
`;

const RowDetail = styled.span`
  font-size: 12px;
  color: var(--color-text-tertiary);
`;

const Fix = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
  flex-shrink: 0;
`;

type Props = {
  communityId?: string;
  community: any;
};

const storageKey = (id: string) => `launchChecklist:${id}:collapsed`;

const LaunchChecklist = ({ communityId, community }: Props) => {
  const history = useHistory();
  const { loading, readiness } = useLaunchReadiness(communityId, community);

  // Default to collapsed: the strip stays out of the way until the coach opens
  // it. Only an explicit "0" (they chose to keep it open) starts expanded.
  const [collapsed, setCollapsed] = useState(true);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    setCollapsed(localStorage.getItem(storageKey(communityId)) !== "0");
  }, [communityId]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (communityId) {
        localStorage.setItem(storageKey(communityId), next ? "1" : "0");
      }
      return next;
    });
  };

  // Nothing to show before the first read, without a community, or once done.
  if (!communityId) return null;
  if (loading && !readiness) return null;
  if (!readiness || readiness.complete) return null;

  const { score, groups } = readiness;
  const left = score.total - score.ready;

  const onRow = (chk: LaunchCheck) => {
    if (chk.route && !chk.manual) history.push(chk.route);
  };

  const renderRow = (chk: LaunchCheck) => {
    const done = chk.status === "ready";
    const clickable = !!chk.route && !chk.manual;
    return (
      <Row
        key={chk.key}
        clickable={clickable}
        onClick={() => onRow(chk)}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (clickable && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onRow(chk);
          }
        }}
      >
        {done ? (
          <CheckCircleIcon
            style={{ fontSize: 16, color: "var(--color-success)" }}
          />
        ) : (
          <Dot status={chk.status} aria-hidden />
        )}
        <RowText>
          <RowItem done={done}>{chk.item}</RowItem>
          <RowDetail>{chk.detail}</RowDetail>
        </RowText>
        {!done && clickable && (
          <Fix>
            Fix
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </Fix>
        )}
      </Row>
    );
  };

  // Split into what is left (grouped, shown first) and what is done (hidden
  // behind a toggle) so the open panel reads as "here is what to do next".
  const remaining = groups
    .map((g) => ({
      title: g.title,
      checks: g.checks.filter((c) => c.status !== "ready"),
    }))
    .filter((g) => g.checks.length > 0);
  const doneChecks = groups.flatMap((g) =>
    g.checks.filter((c) => c.status === "ready")
  );

  return (
    <Card>
      <HeaderBtn onClick={toggle} aria-expanded={!collapsed}>
        <HeaderText>
          <Title>Launch your app</Title>
          <Sub>
            {left} step{left === 1 ? "" : "s"} left
          </Sub>
        </HeaderText>
        <HeaderRight>
          <Bar aria-hidden>
            <Fill pct={score.percent} />
          </Bar>
          <Count>
            {score.ready}/{score.total}
          </Count>
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </HeaderRight>
      </HeaderBtn>

      {!collapsed && (
        <Body>
          {remaining.map((group) => (
            <div key={group.title}>
              <GroupTitle>{group.title}</GroupTitle>
              {group.checks.map(renderRow)}
            </div>
          ))}

          {doneChecks.length > 0 && (
            <>
              <CompletedToggle
                onClick={() => setShowDone((p) => !p)}
                aria-expanded={showDone}
              >
                {showDone ? (
                  <ExpandLessIcon style={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: 16 }} />
                )}
                {showDone ? "Hide" : "Show"} {doneChecks.length} completed
              </CompletedToggle>
              {showDone && doneChecks.map(renderRow)}
            </>
          )}
        </Body>
      )}
    </Card>
  );
};

export default LaunchChecklist;
